/**
 * KTAS 플레이 (iframe 연동)
 *
 * 흐름 (구 프론트 `session-play.component.ts` 와 동일):
 * 1) 플랫폼 API(api2) `GET scenario-ktas/new/{mode}` + `x-class-id` → play_token 발급
 * 2) `https://ktas-app.medicrew.me/#/v1?playToken&apiUrl&parentUrl` 를 iframe 으로 연다
 * 3) iframe 앱이 `/scenario-ktas/start` 이후 요청을 직접 호출하고, 종료 시 `closeIframe` postMessage 를 보낸다
 */

import { getStudyResultPlatformBaseUrl } from '@/config/studyResultPlatformBaseUrl';
import { PlatformApiError, platformGet } from './platformApiClient';

/** KTAS iframe 앱 주소 (해시 라우터 `/#/v1`) */
const DEFAULT_KTAS_APP_URL = 'https://ktas-app.medicrew.me/#/v1';

export function getKtasAppUrl(): string {
	return process.env.REACT_APP_KTAS_APP_URL?.trim() || DEFAULT_KTAS_APP_URL;
}

/** iframe 기본 축척 (0.8 = 브라우저 80% 줌과 비슷하게 더 많은 영역이 보임) */
const DEFAULT_KTAS_IFRAME_SCALE = 0.8;

/**
 * KTAS 앱을 이 비율로 축소해서 보여준다. 앱이 자기 뷰포트(iframe) 높이에 맞춰 레이아웃하므로,
 * iframe 을 1/scale 배로 키운 뒤 scale 로 줄이면 화면이 잘리지 않고 전체가 보인다.
 * `REACT_APP_KTAS_IFRAME_SCALE`(0.5 ~ 1)로 조정, 1 이면 축소 없음.
 */
export function getKtasIframeScale(): number {
	const raw = Number(process.env.REACT_APP_KTAS_IFRAME_SCALE);
	if (Number.isFinite(raw) && raw >= 0.5 && raw <= 1) return raw;
	return DEFAULT_KTAS_IFRAME_SCALE;
}

/** iframe 이 보내는 postMessage 의 origin 검증용 */
export function getKtasAppOrigin(): string {
	try {
		return new URL(getKtasAppUrl()).origin;
	} catch {
		return '';
	}
}

export interface KtasPlayTokenResult {
	/** 암호화·URL 인코딩된 play token (그대로 iframe 쿼리에 전달) */
	play_token: string;
	scenario_id?: string;
	/** true 면 끝나지 않은 플레이가 있음 → 이어하기/새로하기 선택 */
	unfinished?: boolean;
}

/**
 * KTAS play token 발급
 * @param mode 실습 모드 값 (1: 학습, 2: 연습, 3: 평가) — 시나리오 `modeValue`
 * @param renew true 면 끝나지 않은 기존 플레이를 폐기하고 새로 시작
 */
export async function requestKtasPlayToken(params: {
	classId: string | number;
	mode: string;
	renew?: boolean;
}): Promise<KtasPlayTokenResult> {
	const res = await platformGet<KtasPlayTokenResult>(`scenario-ktas/new/${encodeURIComponent(params.mode)}`, {
		classId: params.classId,
		params: { renew: params.renew === true },
	});
	if (!res || typeof res.play_token !== 'string' || !res.play_token) {
		throw new Error('KTAS play token 응답이 올바르지 않습니다.');
	}
	return res;
}

/**
 * iframe URL 생성
 * - `playToken` 은 서버가 이미 encodeURIComponent 한 값 → URLSearchParams 로 한 번 더 인코딩됨 (구 프론트와 동일)
 * - `apiUrl` 은 iframe 이 `decodeURIComponent` 로 푸는 값이라 한 번 인코딩해서 전달
 *   (api.medicrew.me 가 아니라 `scenario-ktas` 가 있는 플랫폼 API 베이스)
 */
export function buildKtasIframeUrl(playToken: string): string {
	const query = new URLSearchParams({
		playToken,
		apiUrl: encodeURIComponent(getStudyResultPlatformBaseUrl()),
		parentUrl: window.location.origin,
	}).toString();
	return `${getKtasAppUrl()}?${query}`;
}

/** 서버 에러 메시지 추출 (`{ statusCode, errMsg }`) */
export function getKtasErrorMessage(error: unknown, fallback: string): string {
	if (error instanceof PlatformApiError) {
		const data = error.data;
		if (data && typeof data === 'object' && 'errMsg' in data) {
			const msg = (data as { errMsg?: unknown }).errMsg;
			if (typeof msg === 'string' && msg.trim()) return msg.trim();
		}
	}
	if (error instanceof Error && error.message.trim()) return error.message.trim();
	return fallback;
}

/** 이 커리큘럼 시나리오가 KTAS 인지 (시나리오 `key` = 콘텐츠 코드) */
export function isKtasScenarioKey(key: unknown): boolean {
	return typeof key === 'string' && key.trim().toLowerCase() === 'ktas';
}
